// src/services/firestore/usersService.js
import { app, auth, serverTimestamp } from '@/firebase'
import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  getFirestore,
  doc, getDoc, setDoc, updateDoc, deleteDoc,
  collection, getDocs, query, where
} from 'firebase/firestore'

const db = getFirestore(app)
const fns = getFunctions(app, 'us-central1')
const listMentionableUsersCallable = httpsCallable(fns, 'listMentionableUsers')

/* ------------------------------ helpers ------------------------------ */
const lc = s => (s || '').toString().trim().toLowerCase()

function b64FromBytes(bytes){
  let bin = ''
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
  return btoa(bin)
}
async function sha256Base64(text){
  const enc = new TextEncoder().encode(text)
  const buf = await crypto.subtle.digest('SHA-256', enc)
  return b64FromBytes(new Uint8Array(buf))
}
function randomBase64(len = 16){
  const bytes = new Uint8Array(len)
  crypto.getRandomValues(bytes)
  return b64FromBytes(bytes)
}
async function hashPasswordWithSalt(password, salt){
  return sha256Base64(`${salt}|${password}`)
}
function nowTsFields(){
  const ts = serverTimestamp()
  return { updatedAt: ts, updatedAtMs: Date.now() }
}

/* ---------------- utility ---------------- */
export function generateTempPassword() {
  const syll = ['na','ne','ni','no','nu','la','le','li','lo','lu','va','ve','vi','vo','vu','ka','ke','ki','ko','ku','sa','se','si','so','su']
  const pick = () => syll[Math.floor(Math.random()*syll.length)] + syll[Math.floor(Math.random()*syll.length)]
  const pad2 = n => String(n).padStart(2,'0')
  return `${pick()}-${pick()}-${pick()}${pad2(Math.floor(Math.random()*100))}`
}

/* ------------------------------- reads ------------------------------- */
/** SELF profile by EMAIL-KEY (canonical: users/{emailLower}) */
export async function getMyProfileFS(){
  const em = lc(auth.currentUser?.email)
  if (!em) throw new Error('Not signed in.')
  const snap = await getDoc(doc(db, 'users', em))
  return snap.exists() ? ({ id: snap.id, ...snap.data() }) : null
}

/** Get a user by doc id (emailLower). */
export async function getUserByIdFS(idOrEmailLower){
  const id = lc(idOrEmailLower)
  if (!id) return null
  const snap = await getDoc(doc(db, 'users', id))
  return snap.exists() ? ({ id: snap.id, ...snap.data() }) : null
}

/** Legacy/random-id support: query by email field. */
export async function getUserByEmailFS(email){
  const em = lc(email)
  if (!em) return null
  const q = query(collection(db, 'users'), where('email', '==', em))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return { id: d.id, ...d.data() }
}

/** Admin list */
export async function listUsersFS(){
  const snap = await getDocs(collection(db, 'users'))
  return snap.docs.map(d => ({ id: d.id, ...d.data() }))
}

export async function listMentionableUsersFS(){
  const { data } = await listMentionableUsersCallable({})
  return Array.isArray(data?.users) ? data.users : []
}

/* ------------------------------ writes ------------------------------ */
export async function upsertUserFS(user){
  const id = (user.id && lc(user.id)) || (user.email && lc(user.email))
  if (!id) throw new Error('upsertUserFS requires user.id or user.email')

  const { id: _omit, ...body } = user
  const emailLower = lc(body.email || id)
  const docBody = {
    ...body,
    email: emailLower,
    emailLower,
    isActive: body.isActive !== false,
    ...nowTsFields(),
  }
  if (!body.createdAt) docBody.createdAt = serverTimestamp()
  await setDoc(doc(db, 'users', id), docBody, { merge: true })
  return id
}

export async function ensureMirrorUserDocByEmailLower(profile){
  const em = lc(profile?.email)
  if (!em) return
  const ref = doc(db, 'users', em)
  const snap = await getDoc(ref)
  const base = {
    email: em,
    emailLower: em,
    displayName: profile?.displayName || '',
    title: profile?.title || '',
    adminLevel: profile?.adminLevel || 'none',
    roles: Array.isArray(profile?.roles) ? profile.roles : [],
    communities: Array.isArray(profile?.communities) ? profile.communities : [],
    allCommunities: profile?.allCommunities === true,
    isActive: profile?.isActive !== false,
    ...nowTsFields(),
  }
  if (!snap.exists()){
    await setDoc(ref, { ...base, createdAt: serverTimestamp() }, { merge: true })
  } else {
    await setDoc(ref, base, { merge: true })
  }
}

/* ------------------------ authentication helpers ------------------------ */
export async function verifyFirestoreCredentials(email, password){
  const em = lc(email)
  const user = await getUserByIdFS(em) || await getUserByEmailFS(em)
  if (!user) throw new Error('Account not found.')
  if (user.isActive === false || user.status === 'archived') {
    throw new Error('Account is inactive.')
  }
  const { passwordSalt: salt, passwordHash: hash } = user
  if (!salt || !hash) throw new Error('Password not set. Contact an admin.')
  const computed = await hashPasswordWithSalt(password, salt)
  if (computed !== hash) throw new Error('Invalid password.')
  return user
}

export async function userChangePasswordFS(email, currentPassword, newPassword){
  const em = lc(email)
  if (!em) throw new Error('Missing email.')
  const ref = doc(db, 'users', em)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Account not found.')

  const data = snap.data() || {}
  const ok = data.passwordSalt && data.passwordHash
    ? (await hashPasswordWithSalt(currentPassword, data.passwordSalt)) === data.passwordHash
    : (currentPassword === 'password')
  if (!ok) throw new Error('Current password is incorrect.')

  const newSalt = randomBase64(16)
  const newHash = await hashPasswordWithSalt(newPassword, newSalt)
  await updateDoc(ref, {
    passwordSalt: newSalt,
    passwordHash: newHash,
    requirePasswordChange: false,
    ...nowTsFields(),
  })
  return true
}

export async function adminSetTempPasswordFS(email, tempPassword){
  const em = lc(email)
  if (!em) throw new Error('Missing email.')
  const ref = doc(db, 'users', em)

  const salt = randomBase64(16)
  const hash = await hashPasswordWithSalt(tempPassword, salt)

  await setDoc(ref, {
    email: em,
    emailLower: em,
    passwordSalt: salt,
    passwordHash: hash,
    requirePasswordChange: true,
    tempPasswordIssuedAt: serverTimestamp(),
    isActive: true,
    createdAt: serverTimestamp(),
    ...nowTsFields(),
  }, { merge: true })

  return true
}

export async function adminChangePasswordFS(email, newPassword, { forceChangeOnNextLogin = false } = {}){
  const em = lc(email)
  if (!em) throw new Error('Missing email.')
  const ref = doc(db, 'users', em)

  const newSalt = randomBase64(16)
  const newHash = await hashPasswordWithSalt(newPassword, newSalt)
  await setDoc(ref, {
    email: em,
    emailLower: em,
    passwordSalt: newSalt,
    passwordHash: newHash,
    requirePasswordChange: !!forceChangeOnNextLogin,
    ...nowTsFields(),
  }, { merge: true })

  return true
}

export async function adminCreateOrInviteUserFS({
  email,
  displayName = '',
  title = '',
  roles = [],
  communities = [],
  isActive = true,
  tempPassword = 'Temp1234!',
}){
  const em = lc(email)
  if (!em) throw new Error('Missing email.')

  await upsertUserFS({
    email: em,
    displayName,
    title,
    roles: Array.isArray(roles) ? roles : [],
    communities: Array.isArray(communities) ? communities : [],
    isActive
  })

  await adminSetTempPasswordFS(em, tempPassword)
  return em
}

export async function seedPasswordIfMissingFS(email, password = 'password'){
  const em = lc(email)
  if (!em) throw new Error('Missing email.')
  const ref = doc(db, 'users', em)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Account not found.')

  const data = snap.data() || {}
  if (data.passwordSalt && data.passwordHash) return false

  const salt = randomBase64(16)
  const hash = await hashPasswordWithSalt(password, salt)
  await updateDoc(ref, {
    passwordSalt: salt,
    passwordHash: hash,
    ...nowTsFields(),
  })
  return true
}

/* ---------------- admin lifecycle / attributes ---------------- */
export async function setUserActiveFS(email, isActive){
  const em = lc(email)
  await updateDoc(doc(db, 'users', em), { isActive: !!isActive, ...nowTsFields() })
  return true
}
export async function setUserRolesFS(email, roles){
  const em = lc(email)
  await updateDoc(doc(db, 'users', em), {
    roles: Array.isArray(roles) ? roles : [],
    ...nowTsFields()
  })
  return true
}
export async function setUserCommunitiesFS(email, communities){
  const em = lc(email)
  await updateDoc(doc(db, 'users', em), {
    communities: Array.isArray(communities) ? communities : [],
    ...nowTsFields()
  })
  return true
}
export async function hardDeleteUserFS(email){
  const em = lc(email)
  await deleteDoc(doc(db, 'users', em))
  return true
}

/* ---------------- convenience ------------------ */
export async function normalizeUserRecordFS(email, { backfillPasswordIfMissing = false } = {}){
  const em = lc(email)
  const ref = doc(db, 'users', em)
  const snap = await getDoc(ref)
  if (!snap.exists()) return false

  const data = snap.data() || {}
  const updates = {}

  if (lc(data.email) !== em) updates.email = em
  if (lc(data.emailLower) !== em) updates.emailLower = em
  if (typeof data.isActive !== 'boolean') updates.isActive = true

  if (Object.keys(updates).length){
    await updateDoc(ref, { ...updates, ...nowTsFields() })
  }

  if (backfillPasswordIfMissing && !(data.passwordSalt && data.passwordHash)){
    await seedPasswordIfMissingFS(em)
  }

  return true
}
